import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import * as Sentry from "@sentry/react";
import { type ParsedLocation, redirect } from "@tanstack/react-router";
import { getDefaultStore } from "jotai";
import { RESET } from "jotai/utils";

import { AuthProvider } from "@/enums/auth-provider";
import { deepLinkAtom } from "@/lib/atoms/deep-link-atom";
import { resolvedUserAtom } from "@/lib/atoms/resolved-user-atom";
import {
  captureSessionMigrationError,
  trackSessionMigrationFailed,
  trackSessionMigrationStarted,
  trackSessionMigrationSucceeded,
} from "@/lib/auth/auth-tracking";
import type { AuthenticationClient } from "@/lib/auth/authentication-client";
import { migrateSession } from "@/lib/auth/migrate-session";
import { SessionHydrationError } from "@/lib/auth/session-hydration-error";
import {
  logLogoutAccessDenied,
  logLogoutInvalidUser,
  logNotAuthenticated,
} from "@/lib/breadcrumbs";
import { getAppInfo } from "@/lib/utils/app-info";
import type { RootRouteContext } from "@/routes/-root/-root-context";
import { getUserData } from "@/routes/-root/root-get-user-data";
import { newSessionGuards, routeGuards, runGuards } from "@/routes/-root/root-guards";

// ---------------------------------------------------------------------------
// Helpers (pre-user-load)
// ---------------------------------------------------------------------------

const defaultStore = getDefaultStore();

const logoutAndThrowInvalidUser = async (
  authClient: AuthenticationClient,
): Promise<never> => {
  defaultStore.set(resolvedUserAtom, RESET);
  await authClient.logout();
  throw new Error("Usuário não autorizado.");
};

const logoutAndThrowAccessDenied = async (
  authClient: AuthenticationClient,
  cause: string,
): Promise<never> => {
  defaultStore.set(resolvedUserAtom, RESET);
  await authClient.logout();
  throw new Error("Acesso Negado", { cause });
};

const redirectToDeepLink = (location: ParsedLocation): void => {
  const deepLinkUrl = defaultStore.get(deepLinkAtom);

  if (deepLinkUrl && location.pathname !== deepLinkUrl) {
    defaultStore.set(deepLinkAtom, undefined);
    redirect({ to: deepLinkUrl, throw: true });
  } else if (deepLinkUrl) {
    defaultStore.set(deepLinkAtom, undefined);
  }
};

const setupLaunchDarklyContexts = async (context: RootRouteContext): Promise<void> => {
  if (context.ldClient.userContextAlreadyAdded()) return;

  const appInfo = await getAppInfo();
  const platform = Capacitor.getPlatform();
  const { identifier: deviceId } = await Device.getId();

  await context.ldClient.addContexts([
    { kind: "user", key: `${context.user.codUsuario}` },
    {
      kind: "hasTrialAssist",
      key: String(context.user.possivelDataFimTrialAssistPro.temTrialDisponivel),
    },
    { kind: "device", key: deviceId, platform, version: appInfo.version },
  ]);

  // Re-apply the IP to the freshly identified user context (cached; no network).
  await context.ldClient.ensureUserIp();
};

const identifySentryUser = (context: RootRouteContext, sub: string): void => {
  Sentry.setUser({
    id: context.user.codUsuario,
    username: sub,
    email: context.user.email,
    fullName: context.user.apelido ?? `${context.user.nome} ${context.user.sobrenome}`,
  });
};

const identifySentryAnonymous = (sub: string): void => {
  Sentry.setUser({ username: sub });
};

// ---------------------------------------------------------------------------
// Auth0 → Stytch silent session migration
// ---------------------------------------------------------------------------

let migrationAttempted = false;

/** @internal Exposed for testing only. */
export const resetMigrationAttempted = (): void => {
  migrationAttempted = false;
};

const migrateAuth0SessionToStytch = async (context: RootRouteContext): Promise<void> => {
  if (migrationAttempted) {
    return;
  }

  if (context.authClient.activeProvider !== AuthProvider.Auth0) {
    return;
  }

  // Claim the migration synchronously, before the first await below. JS is
  // single-threaded, so no other task can interleave between the guard check
  // above and this assignment — which keeps the check-and-set atomic. Without
  // this, concurrent rootBeforeLoad invocations (e.g. during the login redirect)
  // would both pass the guard while suspended on the await and migrate twice.
  migrationAttempted = true;

  // Wait for the persisted Stytch session to be hydrated into memory before
  // checking isSessionActive(). Otherwise, on a cold start this check races the
  // fire-and-forget initialize() and reads an empty in-memory session, causing a
  // redundant migration even though a valid session already exists in storage.
  await context.stytchAuthenticationClient.hydrationReady;

  const existingTokens = context.stytchAuthenticationClient.isSessionActive();

  if (existingTokens) {
    return;
  }

  trackSessionMigrationStarted();

  try {
    const auth0Token = await context.auth0AuthenticationClient.getTokenSilently();

    const { sessionToken, sessionJwt } = await migrateSession(
      auth0Token,
      context.auth0AuthenticationClient,
    );

    const hydrated = await context.stytchAuthenticationClient.hydrateSessionWithTokens(
      sessionToken,
      sessionJwt,
    );

    if (!hydrated) {
      throw new SessionHydrationError();
    }

    trackSessionMigrationSucceeded();
  } catch (error) {
    // Migration failure is non-blocking — user continues with Auth0 session.
    // Will not be retried until the next app session (cold start).
    trackSessionMigrationFailed(error);
    const auth0Sub = await context.auth0AuthenticationClient
      .getUserSub()
      .catch(() => undefined);
    captureSessionMigrationError(error, auth0Sub);
  }
};

// ---------------------------------------------------------------------------
// Main beforeLoad
// ---------------------------------------------------------------------------

export const rootBeforeLoad = async (
  context: RootRouteContext,
  location: ParsedLocation,
): Promise<void> => {
  if (location.pathname.includes("/auth")) {
    return;
  }

  // Must run before any flag is read so IP-based targeting is honored.
  await context.ldClient.ensureUserIp();

  redirectToDeepLink(location);

  let isAuthenticated = await context.authClient.isAuthenticated();
  const accessDenied = "accessDenied" in location.search && location.search.accessDenied;

  if (!isAuthenticated && accessDenied) {
    const cause = "cause" in location.search ? String(location.search.cause) : "";
    logLogoutAccessDenied({ location, reason: cause });
    await logoutAndThrowAccessDenied(context.authClient, cause);
  }

  // Both conditions are compile-time constants (false/null outside the dev
  // server), so production builds drop this branch and the dynamic import.
  if (!isAuthenticated && import.meta.env.DEV && __DEV_AUTOLOGIN__) {
    const { devAutoLogin } = await import("@/lib/auth/dev-auto-login");
    isAuthenticated = await devAutoLogin(__DEV_AUTOLOGIN__);
  }

  if (!isAuthenticated) {
    logNotAuthenticated({ location });
    redirect({
      to: "/auth/landing",
      search: { returnTo: location.pathname },
      throw: true,
    });
  }

  await migrateAuth0SessionToStytch(context);

  const sub = await context.authClient.getUserSub();

  if (sub) {
    identifySentryAnonymous(sub);
  }

  const isValidRole = await context.authClient.hasValidRole();
  if (!isValidRole) {
    logLogoutInvalidUser({ location, reason: "Invalid Role" });
    await logoutAndThrowInvalidUser(context.authClient);
  }

  const oldUser = context.user;
  context.user = await getUserData({ context, sub });
  defaultStore.set(resolvedUserAtom, context.user);

  if (oldUser.codUsuario !== context.user.codUsuario) {
    identifySentryUser(context, sub);

    await setupLaunchDarklyContexts(context);

    await runGuards(newSessionGuards, { context, location });
  }

  await runGuards(routeGuards, { context, location });
};
